// Typed client for AI Playground planning + conversations (AgentPlanningController,
// api/agents/drafts and api/agents/conversations; standing-agents D16/D17). A prompt
// makes a DRAFT, never a run; Start on the confirmed plan creates the run. Every route
// 404s while the region's `standing.enabled` switch is off — `getWaitingCount` reports
// that as `enabled: false` so the page keeps the legacy composer.

import type {
  AgentRunDraft,
  WorkflowDetail,
  ConversationDetail,
  ConversationStatus,
  ConversationSummary,
  CreateDraftRequest,
  EditPlanRequest,
  PlanningProblem,
  RoutingDirectory,
} from '@shared/api-types';
import { ApiError, apiFetch } from './apiClient';

const DRAFTS = '/api/agents/drafts';
const CONVERSATIONS = '/api/agents/conversations';

/** Plan a prompt (a new conversation, or the next step of `conversationId`). */
export const createDraft = (body: CreateDraftRequest) => apiFetch<AgentRunDraft>(DRAFTS, { method: 'POST', body });

/** Read a draft. */
export const getDraft = (id: string, signal?: AbortSignal) => apiFetch<AgentRunDraft>(`${DRAFTS}/${id}`, { signal });

/** Answer Orion's question (a quick reply's `optionId`, or typed `text`). */
export const replyToDraft = (id: string, questionId: string, answer: { optionId?: string; text?: string }) =>
  apiFetch<AgentRunDraft>(`${DRAFTS}/${id}/reply`, { method: 'POST', body: { questionId, ...answer } });

/** A direct edit of the plan. */
export const editPlan = (id: string, body: EditPlanRequest) =>
  apiFetch<AgentRunDraft>(`${DRAFTS}/${id}/plan`, { method: 'PATCH', body });

/** Start the plan version the operator saw. */
export const startDraft = (id: string, planVersion: number) =>
  apiFetch<{ runId: string; draft: AgentRunDraft }>(`${DRAFTS}/${id}/start`, { method: 'POST', body: { planVersion } });

/** Cancel an open draft. */
export const cancelDraft = (id: string) => apiFetch<AgentRunDraft>(`${DRAFTS}/${id}`, { method: 'DELETE' });

/** Runners and companies (with nicknames) for @ suggestions and routing chips. */
export const getRoutingDirectory = (signal?: AbortSignal) => apiFetch<RoutingDirectory>('/api/agents/routing-directory', { signal });

export interface ConversationQuery {
  scope?: 'mine' | 'workspace';
  search?: string;
  status?: ConversationStatus | '';
  archived?: boolean;
}

/** The conversation list. */
export function listConversations(q: ConversationQuery = {}, signal?: AbortSignal) {
  const params = new URLSearchParams();
  if (q.scope === 'workspace') params.set('scope', 'workspace');
  if (q.search?.trim()) params.set('search', q.search.trim());
  if (q.status) params.set('status', q.status);
  if (q.archived) params.set('archived', 'true');
  const qs = params.toString();
  return apiFetch<ConversationSummary[]>(qs ? `${CONVERSATIONS}?${qs}` : CONVERSATIONS, { signal });
}

/** The thread. */
export const getConversation = (id: string, signal?: AbortSignal) =>
  apiFetch<ConversationDetail>(`${CONVERSATIONS}/${id}`, { signal });

/** Rename, pin or archive (owner only). */
export const updateConversation = (id: string, body: { title?: string; pinned?: boolean; archived?: boolean }) =>
  apiFetch<ConversationSummary>(`${CONVERSATIONS}/${id}`, { method: 'PATCH', body });

/** Rerun: the conversation's original plan, re-checked, in a new conversation waiting for Start. */
export const rerunConversation = (id: string) => apiFetch<AgentRunDraft>(`${CONVERSATIONS}/${id}/rerun`, { method: 'POST' });

/** Convert the conversation's finished steps into a new draft workflow (one agent node per step). */
export const convertConversationToWorkflow = (id: string, body: { name: string; folderId?: string | null }) =>
  apiFetch<WorkflowDetail>(`/api/agents/conversations/${id}/convert-to-workflow`, { method: 'POST', body });

/** The nav count; `enabled: false` when planning is off in this region. */
export const getWaitingCount = (signal?: AbortSignal) =>
  apiFetch<{ enabled: boolean; count: number }>(`${CONVERSATIONS}/waiting-count`, { signal });

/** The 409/410 body of a planning call, when that's what failed. */
export function planningProblem(err: unknown): PlanningProblem | null {
  if (err instanceof ApiError && (err.status === 409 || err.status === 410 || err.status === 400) && err.body && typeof err.body === 'object') {
    return err.body as PlanningProblem;
  }
  return null;
}
